/**
 * useLiveMatchSocket — subscribe a component to live match updates.
 *
 * Behaviour:
 *   1. Opens a WebSocket to /api/v1/events/{eventId}/live/{fixtureId}/ws.
 *   2. Pings the server every 20 s so Kubernetes ingress / CDN proxies keep
 *      the connection warm (backend echoes a heartbeat).
 *   3. On broadcast messages (ball / wicket / innings_end / super_over_start /
 *      match_end) invokes `onUpdate()` so the host component refetches data.
 *   4. Auto-reconnects on close using exponential back-off (1s, 2s, 4s, 8s,
 *      capped at 15s).
 *   5. If the socket fails to open or stays disconnected for >10 s, falls back
 *      to 30 s REST polling via the same `onUpdate()` callback so users are
 *      never stuck on a stale score.
 */
import { useEffect, useRef, useState } from 'react';

const RELEVANT_TYPES = new Set([
  'ball', 'wicket', 'innings_start', 'innings_end',
  'super_over_start', 'match_end', 'match_start',
  'match_awards_ready',
  'poll',
]);

export default function useLiveMatchSocket({ eventId, fixtureId, enabled = true, onUpdate }) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const attemptRef = useRef(0);
  // Keep onUpdate in a ref so effect deps stay stable and it always runs latest logic
  const updateRef = useRef(onUpdate);
  useEffect(() => { updateRef.current = onUpdate; }, [onUpdate]);

  useEffect(() => {
    if (!enabled || !eventId || !fixtureId) return undefined;

    let cancelled = false;

    const clearPing = () => {
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
    };
    const clearReconnect = () => {
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    };
    const clearPoll = () => {
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    };

    const startPolling = () => {
      if (pollTimerRef.current) return;
      // Gentle 30 s fallback so viewers always see fresh data even without WS
      pollTimerRef.current = setInterval(() => {
        try { updateRef.current && updateRef.current({ type: 'poll' }); } catch { /* noop */ }
      }, 30000);
    };

    const open = () => {
      if (cancelled) return;
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const wsBase = base.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:');
      const url = `${wsBase}/api/v1/events/${eventId}/live/${fixtureId}/ws`;

      let ws;
      try { ws = new WebSocket(url); }
      catch { scheduleReconnect(); return; }

      wsRef.current = ws;

      // If the socket doesn't open within 10 s, start the REST polling fallback
      const openTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) startPolling();
      }, 10000);

      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch { /* noop */ } return; }
        clearTimeout(openTimeout);
        attemptRef.current = 0;
        setConnected(true);
        // Stop the polling fallback once WS is live
        clearPoll();
        // Client-side ping every 20 s
        clearPing();
        pingTimerRef.current = setInterval(() => {
          try {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          } catch { /* noop */ }
        }, 20000);
      };

      ws.onmessage = (evt) => {
        let data = null;
        try { data = JSON.parse(evt.data); } catch { return; }
        if (!data || data.type === 'heartbeat') return;
        if (RELEVANT_TYPES.has(data.type)) {
          try { updateRef.current && updateRef.current(data); } catch { /* noop */ }
        }
      };

      const handleClose = () => {
        clearTimeout(openTimeout);
        clearPing();
        setConnected(false);
        if (!cancelled) scheduleReconnect();
      };
      ws.onclose = handleClose;
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      // Start polling fallback while we back off
      startPolling();
      const n = Math.min(attemptRef.current, 4);
      const delay = Math.min(1000 * Math.pow(2, n), 15000);
      attemptRef.current += 1;
      clearReconnect();
      reconnectTimerRef.current = setTimeout(open, delay);
    };

    open();

    return () => {
      cancelled = true;
      clearPing();
      clearReconnect();
      clearPoll();
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
      setConnected(false);
    };
  }, [eventId, fixtureId, enabled]);

  return { connected };
}
